// 汎用CRUDマスタ画面：マスタごとの列定義 / フィールド定義 / コレクションキー で駆動する。
// MasterListPage の中で `key={masterKey}` を渡して使うことを想定し、見出しは持たない。
import { useEffect, useMemo, useState } from "react";
import {
  Box, Stack, Typography, Paper, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, MenuItem, Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Time from "../../../util/Time";
import { ExtendedDB } from "../../../types/extendedDB";
import { useFullDb, replaceCollections, nextIdFor, slicePatchOfRecords, SlicePatch, SliceKey, SliceRow } from "../../../api/dbSlice";
import { useIsOnline } from "../../../util/useIsOnline";
import { usePartitionFilter } from "../../../util/partitionFilterStore";
import { errorMessageOf, objectKeys, readProp } from "../../../util/typed";
import { buildDivisionDuplication, collectDivisionChildRows } from "./divisionDuplication";
import type { AreaDivisionLocationLabelBase } from "../../../schema/generated/AreaDivisionLocationLabel";
import type { LocationDivisionBase } from "../../../schema/generated/LocationDivision";
import type { LocationDivisionParentPathBase } from "../../../schema/generated/LocationDivisionParentPath";
import { PageContext } from "../PageContext";
import { ContentLoading } from "../../../components/Loading";

// 区分マスタの複製／削除ダイアログで関連レコード件数を人間向け名前で表示する
const COLLECTION_LABEL: Partial<Record<keyof ExtendedDB, string>> = {
  workTypes: "工種",
  workSubtypes: "種別",
  workDetails: "工程",
  constructionMethods: "施工方法",
  stirringMethods: "撹拌方法",
  carryInLocations: "搬入場所",
  materials: "材料",
  materialDetails: "材料細目",
  potLifeSpecs: "可使時間",
  qualityMeasurementItems: "品質測定項目",
  mixRatioDefinitions: "配合比",
  mixRatioDetails: "配合比明細",
  locationDivisions: "場所区分",
};

// 整数主キーマスタ行の共通形(汎用CRUDが静的に扱う上限型)。
export type MasterRowBounds = { id: number; createdAt: number; updatedAt: number; deleted_at: number | null };

// 編集ドラフトにだけ乗る一時フィールド(保存時に本体から除去し、対応コレクションへ同期される)。
// パーティションキーも optional で持たせ、新規作成時の初期値設定を型付きで行う。
export type MasterDraftTransients = {
  location_labels?: Array<Pick<AreaDivisionLocationLabelBase, "slot" | "label_name" | "is_required">>;
  parent_path_draft?: Record<number, string>;
  area_division_id?: number;
  material_division_id?: number;
};

type PartitionKey = "area_division_id" | "material_division_id";

// 行が論理削除済みか(deleted_at != null)を判定する。
function isDeleted(row: { deleted_at?: number | null } | null | undefined): boolean {
  return row != null && row.deleted_at != null;
}

// 動的キー駆動の箇所で、行からパーティションキー値を読む(number 以外は「無し」扱い)。
// eslint-disable-next-line @typescript-eslint/ban-types -- ジェネリクスの「非プリミティブ」制約としての object は正当(値の型としては使わない)
function readNumberProp(row: object, key: string): number | null {
  const value = readProp(row, key);
  if (typeof value === "number") {
    return value;
  }
  return null;
}

// 区分マスタ行の name(bounds 型に無い動的フィールド)を表示用に読む。
// eslint-disable-next-line @typescript-eslint/ban-types -- ジェネリクスの「非プリミティブ」制約としての object は正当(値の型としては使わない)
function readNameProp(row: object): string {
  const value = readProp(row, "name");
  if (typeof value === "string") {
    return value;
  }
  return "";
}

export type MasterOptionItem = { value: string | number; label: string };

// コールバックは全てメソッド構文で宣言する。メソッドはバイバリアントに検査されるため、
// 行型付き config(MasterCrudConfig<AreaDivisionBase> 等)を消去型(MasterCrudConfig)の一覧へ
// 表明なしで格納できる(プロパティ関数型のままだと引数の反変性で弾かれる)。
export type ColumnDef<T = MasterRowBounds> = {
  // keyof T | string は string へ吸収され検査効果が無い上、keyof T の参照が変性計算で T を
  // 不変にしてメソッド緩和(消去型への無キャスト代入)を打ち消すため、素の string で持つ。
  key: string;
  label: string;
  width?: number | string;
  render?(row: T, db: ExtendedDB): React.ReactNode;
};

export type FieldDef<T = MasterRowBounds> = {
  key: string;
  label: string;
  type: "text" | "number" | "boolean" | "select" | "multiline" | "custom";
  required?: boolean;
  // union に関数型を含むとメソッド緩和が効かないため、resolve メソッドを持つオブジェクトに統一する
  // (宣言側は配列/関数のまま書け、defineMasterConfig が正規化する)。
  options?: { resolve(db: ExtendedDB, draft?: T & MasterDraftTransients): MasterOptionItem[] };
  helperText?: string;
  placeholder?: string;
  visible?(draft: T & MasterDraftTransients, db: ExtendedDB): boolean;
  customRender?(params: { draft: T & MasterDraftTransients; setDraft(next: T & MasterDraftTransients): void; db: ExtendedDB }): React.ReactNode;
};

export type MasterCrudConfig<T extends MasterRowBounds = MasterRowBounds> = {
  collectionKey: keyof ExtendedDB;
  title: string;
  description?: string;
  columns: ColumnDef<T>[];
  fields: FieldDef<T>[];
  newDefaults(db: ExtendedDB): Partial<T>;
  idPrefix: string;
  customEditContent?(params: { draft: T & MasterDraftTransients; setDraft(next: T & MasterDraftTransients): void; db: ExtendedDB }): React.ReactNode;
  // defineMasterConfig が内包する型付きアクセサ。collectionKey との対応は宣言時に検査済みのため、
  // 画面側は動的キーの読み書き境界(表明)なしでコレクションへアクセスできる。
  collection(db: ExtendedDB): T[];
  patchOf(rows: T[]): SlicePatch;
};

// 整数主キーマスタ行を持つコレクションキー(MasterRowBounds を満たす行のコレクションのみ)。
type IntMasterSliceKey = { [K in SliceKey]: SliceRow<K> extends MasterRowBounds ? K : never }[SliceKey];

// 宣言側の入力形。options は配列/関数のどちらでも書ける(defineMasterConfig が resolve へ正規化)。
export type FieldDefInput<T> = {
  key: string;
  label: string;
  type: FieldDef<T>["type"];
  required?: boolean;
  options?: MasterOptionItem[] | ((db: ExtendedDB, draft?: T & MasterDraftTransients) => MasterOptionItem[]);
  helperText?: string;
  placeholder?: string;
  visible?(draft: T & MasterDraftTransients, db: ExtendedDB): boolean;
  customRender?(params: { draft: T & MasterDraftTransients; setDraft(next: T & MasterDraftTransients): void; db: ExtendedDB }): React.ReactNode;
};

export type MasterCrudConfigInput<K extends IntMasterSliceKey> = {
  collectionKey: K;
  title: string;
  description?: string;
  columns: ColumnDef<SliceRow<K>>[];
  fields: FieldDefInput<SliceRow<K>>[];
  newDefaults(db: ExtendedDB): Partial<SliceRow<K>>;
  idPrefix: string;
  customEditContent?(params: { draft: SliceRow<K> & MasterDraftTransients; setDraft(next: SliceRow<K> & MasterDraftTransients): void; db: ExtendedDB }): React.ReactNode;
};

// options(配列/関数)を resolve メソッド持ちオブジェクトへ正規化する(挙動等価)。
function normalizeFieldOptions<T>(options: FieldDefInput<T>["options"]): FieldDef<T>["options"] {
  if (options === undefined) {
    return undefined;
  }
  if (typeof options === "function") {
    return { resolve: (db, draft) => options(db, draft) };
  }
  return { resolve: () => options };
}

// collectionKey ↔ 行型の対応を宣言時に型検査する config 宣言ヘルパ。
// K のリテラルから行型 SliceRow<K> を導出するため、キーの誤記や行型の取り違えがコンパイルエラーになる。
// あわせて collection / patchOf の型付きアクセサを内包し(単一リテラル K の索引・計算プロパティは
// 表明なしで型が通る)、画面側から動的キー境界を排除する。
export function defineMasterConfig<K extends IntMasterSliceKey>(input: MasterCrudConfigInput<K>): MasterCrudConfig<SliceRow<K>> {
  const fields: FieldDef<SliceRow<K>>[] = input.fields.map((field) => ({
    ...field,
    options: normalizeFieldOptions(field.options),
  }));
  return {
    ...input,
    fields,
    collection: (db) => db[input.collectionKey],
    patchOf: (rows) => ({ [input.collectionKey]: rows }),
  };
}

interface Props<T extends MasterRowBounds> {
  config: MasterCrudConfig<T>;
  pageContext: PageContext;
}

export default function MasterCrudPage<T extends MasterRowBounds>({ config, pageContext }: Props<T>) {
  type Draft = T & MasterDraftTransients;
  const idb = pageContext.api.indexedDB;
  const ownerId = pageContext.api.currentUser.id;
  const { db, loading } = useFullDb(idb, ownerId);
  const isOnline = useIsOnline(pageContext.api);

  // 論理削除されたレコードはテーブル・件数等から除外
  const allRows = config.collection(db);
  const rows = allRows.filter((r) => !isDeleted(r));

  // パーティションキー検出: area_division_id / material_division_id を持つマスタは
  // テーブル上にプルダウンを置いて区分でフィルタする。
  const partitionKey: PartitionKey | null = useMemo(() => {
    const r0 = rows[0];
    if (r0 !== undefined) {
      if ("area_division_id" in r0) return "area_division_id";
      if ("material_division_id" in r0) return "material_division_id";
    }
    const fromFieldKey = config.fields.map((f) => String(f.key)).find((k) => k === "area_division_id" || k === "material_division_id");
    if (fromFieldKey === "area_division_id" || fromFieldKey === "material_division_id") {
      return fromFieldKey;
    }
    try {
      const defaults = config.newDefaults(db);
      if ("area_division_id" in defaults) return "area_division_id";
      if ("material_division_id" in defaults) return "material_division_id";
    } catch {
      /* newDefaults が db 未参照前提でエラーになる可能性は無視 */
    }
    return null;
  }, [rows, config.fields, config.newDefaults, db]);

  const partitionOptions = useMemo<{ value: number; label: string }[]>(() => {
    if (partitionKey === "area_division_id") {
      return db.areaDivisions
        .filter((d) => d.is_active && !isDeleted(d))
        .sort((a, b) => a.display_order - b.display_order)
        .map((d) => ({ value: d.id, label: `#${d.id} ${d.name}` }));
    }
    if (partitionKey === "material_division_id") {
      return db.materialDivisions
        .filter((d) => d.is_active && !isDeleted(d))
        .sort((a, b) => a.display_order - b.display_order)
        .map((d) => ({ value: d.id, label: `#${d.id} ${d.name}` }));
    }
    return [];
  }, [partitionKey, db.areaDivisions, db.materialDivisions]);

  // 区分フィルタはグローバルストアで管理(マスタ切替でも選択を維持)
  const [areaFilter, setAreaFilter] = usePartitionFilter("area_division_id");
  const [materialFilter, setMaterialFilter] = usePartitionFilter("material_division_id");
  const pickFilterValue = (): number | null => {
    if (partitionKey === "area_division_id") {
      return areaFilter;
    }
    if (partitionKey === "material_division_id") {
      return materialFilter;
    }
    return null;
  };
  const filterValue = pickFilterValue();
  const setFilterValue = (v: number | null) => {
    if (partitionKey === "area_division_id") setAreaFilter(v);
    else if (partitionKey === "material_division_id") setMaterialFilter(v);
  };

  useEffect(() => {
    if (!partitionKey || partitionOptions.length === 0) return;
    if (filterValue === null || !partitionOptions.some((o) => o.value === filterValue)) {
      // 件数が最大の区分を初期選択。同数なら id が小さい方
      const counts = new Map<number, number>();
      for (const r of rows) {
        const v = readNumberProp(r, partitionKey);
        if (v !== null) counts.set(v, (counts.get(v) ?? 0) + 1);
      }
      const firstOption = partitionOptions[0];
      if (firstOption === undefined) {
        return;
      }
      let best = firstOption.value;
      let bestCount = -1;
      for (const o of partitionOptions) {
        const c = counts.get(o.value) ?? 0;
        if (c > bestCount) {
          best = o.value;
          bestCount = c;
        }
      }
      setFilterValue(best);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partitionKey, partitionOptions]);

  const visibleRows = useMemo<T[]>(() => {
    if (!partitionKey || filterValue === null) return rows;
    const pk = partitionKey;
    return rows.filter((r) => readNumberProp(r, pk) === filterValue);
  }, [rows, partitionKey, filterValue]);

  // 表示する列: パーティション列はテーブルから隠す(プルダウンで絞り込み済みのため)
  const visibleColumns = useMemo(
    () => config.columns.filter((c) => String(c.key) !== partitionKey),
    [config.columns, partitionKey]
  );

  // 区分マスタ判定(自身が AreaDivision / MaterialDivision の場合、複製・削除に特殊処理)
  const isAreaDivision = config.collectionKey === "areaDivisions";
  const isMaterialDivision = config.collectionKey === "materialDivisions";
  const isDivisionMaster = isAreaDivision || isMaterialDivision;
  // 場所区分マスタ判定(上位区分パス編集・改名追従・削除警告の特殊処理)
  const isLocationDivision = config.collectionKey === "locationDivisions";
  const pickDivisionPartitionKey = (): PartitionKey | null => {
    if (isAreaDivision) return "area_division_id";
    if (isMaterialDivision) return "material_division_id";
    return null;
  };
  const pickDivisionLabel = (): string => {
    if (isAreaDivision) return "工事型区分";
    if (isMaterialDivision) return "材料区分";
    return "";
  };
  const divisionPartitionKey = pickDivisionPartitionKey();
  const divisionLabel = pickDivisionLabel();

  const [editing, setEditing] = useState<Draft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<T | null>(null);
  const [divisionDupConfirm, setDivisionDupConfirm] = useState<T | null>(null);

  // 指定区分ID に紐づく関連レコード(論理削除されていないもの)を全コレクションから集計
  const countRelated = (divId: number) => {
    const entries: Array<{ key: keyof ExtendedDB; label: string; count: number }> = [];
    let total = 0;
    if (!divisionPartitionKey) return { entries, total };
    const dpk = divisionPartitionKey;
    for (const key of objectKeys(db)) {
      const v = db[key];
      if (Array.isArray(v) && key !== config.collectionKey) {
        const collectionRows: Array<Record<string, unknown>> = v;
        const c = collectionRows.filter((r) => readNumberProp(r, dpk) === divId && !isDeleted(r)).length;
        if (c > 0) {
          entries.push({ key, label: COLLECTION_LABEL[key] ?? String(key), count: c });
          total += c;
        }
      }
    }
    return { entries, total };
  };

  // AreaDivision 編集ドラフトに乗る一時フィールド location_labels の初期値を組み立てる。
  // 既存行があればそれを slot=1..10 で穴埋め、新規なら空のスロット10件を用意する。
  const buildLocationLabelsDraft = (areaDivisionId: number | null): Array<Pick<AreaDivisionLocationLabelBase, "slot" | "label_name" | "is_required">> => {
    let existing: AreaDivisionLocationLabelBase[] = [];
    if (areaDivisionId !== null) {
      existing = (db.areaDivisionLocationLabels ?? [])
        .filter((l) => l.area_division_id === areaDivisionId && l.deleted_at == null);
    }
    const bySlot = new Map<number, AreaDivisionLocationLabelBase>();
    for (const l of existing) {
      bySlot.set(l.slot, l);
    }
    const draft: Array<Pick<AreaDivisionLocationLabelBase, "slot" | "label_name" | "is_required">> = [];
    for (let s = 1; s <= 10; s++) {
      const cur = bySlot.get(s);
      draft.push({
        slot: s,
        label_name: cur?.label_name ?? "",
        is_required: cur?.is_required ?? false,
      });
    }
    return draft;
  };

  // 場所区分の編集ドラフトに乗る一時フィールド parent_path_draft の初期値を組み立てる。
  // 既存の locationDivisionParentPaths から ancestor_slot -> ancestor_division_name を復元する。
  const buildParentPathDraft = (locationDivisionId: number | null): Record<number, string> | undefined => {
    if (locationDivisionId === null) return undefined;
    const rows = (db.locationDivisionParentPaths ?? [])
      .filter((p) => p.location_division_id === locationDivisionId && p.deleted_at == null);
    if (rows.length === 0) return undefined;
    const path: Record<number, string> = {};
    for (const p of rows) {
      path[p.ancestor_slot] = p.ancestor_division_name;
    }
    return path;
  };

  // 対象の場所区分を parent_path に含む、有効な子孫 locationDivision を集計する(削除警告用)。
  const findLocationDivisionDescendants = (target: LocationDivisionShape): LocationDivisionBase[] => {
    const allDivisions = db.locationDivisions ?? [];
    const allPaths = db.locationDivisionParentPaths ?? [];
    const descendants: LocationDivisionBase[] = [];
    for (const d of allDivisions) {
      const isCandidate = d.id !== target.id && d.deleted_at == null;
      if (isCandidate) {
        const refersToTarget = allPaths.some(
          (p) => p.location_division_id === d.id && p.deleted_at == null
            && p.ancestor_slot === target.slot && p.ancestor_division_name === target.division_name,
        );
        if (refersToTarget) descendants.push(d);
      }
    }
    return descendants;
  };

  // 場所区分の保存時に、ドラフトの parent_path_draft を locationDivisionParentPaths コレクション
  // 全体へ反映する。空欄化されたスロットは論理削除、既存 slot は id を維持して upsert する。
  // basePaths は改名追従等の他の同時更新をマージ済みの配列を渡す(呼び出し側で1本化するため)。
  const buildSyncedParentPaths = async (
    locationDivisionId: number,
    draftPath: Record<number, string>,
    basePaths: LocationDivisionParentPathBase[],
  ): Promise<LocationDivisionParentPathBase[]> => {
    const nowMs = Time.Now().GetTime();
    const allPaths = basePaths;
    const others = allPaths.filter((p) => p.location_division_id !== locationDivisionId);
    const mine = allPaths.filter((p) => p.location_division_id === locationDivisionId);
    const mineBySlot = new Map<number, LocationDivisionParentPathBase>();
    for (const p of mine) {
      mineBySlot.set(p.ancestor_slot, p);
    }
    const allSlots = new Set<number>([...mineBySlot.keys(), ...Object.keys(draftPath).map(Number)]);
    let nextId = await nextIdFor(idb, ownerId, "locationDivisionParentPaths");
    const synced: LocationDivisionParentPathBase[] = [];
    for (const slot of allSlots) {
      const name = draftPath[slot];
      const prev = mineBySlot.get(slot);
      const isEmpty = name === undefined || name === "";
      if (isEmpty) {
        if (prev && prev.deleted_at == null) {
          synced.push({ ...prev, deleted_at: nowMs, updatedAt: nowMs });
        } else if (prev) {
          synced.push(prev);
        }
      } else if (prev) {
        synced.push({ ...prev, ancestor_division_name: name, deleted_at: null, updatedAt: nowMs });
      } else {
        synced.push({
          id: nextId,
          createdAt: nowMs,
          updatedAt: nowMs,
          location_division_id: locationDivisionId,
          ancestor_slot: slot,
          ancestor_division_name: name,
          deleted_at: null,
        });
        nextId += 1;
      }
    }
    return [...others, ...synced];
  };

  // owner(locationDivisionId)の有効な parent_path を slot -> 区分名 の Map で返す。
  const pathMapFor = (allPaths: LocationDivisionParentPathBase[], locationDivisionId: number): Map<number, string> => {
    const map = new Map<number, string>();
    for (const p of allPaths) {
      if (p.location_division_id === locationDivisionId && p.deleted_at == null) {
        map.set(p.ancestor_slot, p.ancestor_division_name);
      }
    }
    return map;
  };

  // ownerPath が targetAncestorPath(target 自身が持つ上位区分パス、slot < target.slot)を
  // 過不足なく含んでいるか(= prefix 一致)判定する。
  const matchesAncestorPrefix = (ownerPath: Map<number, string>, targetAncestorPath: Map<number, string>): boolean => {
    for (const [slot, name] of targetAncestorPath) {
      if (ownerPath.get(slot) !== name) return false;
    }
    return true;
  };

  // 改名追従: division_name 変更時、同一 area_division_id かつ「旧フルパスの子孫」である
  // locationDivisionParentPaths の ancestor_division_name を新名称へ更新する。
  // 「旧フルパス prefix 一致」= target 自身の上位区分パス(slot < target.slot)を、
  // 候補行の owner が全く同じ値で持っている場合のみ対象にする。これにより、
  // 同一 slot・同一名称が別系統(上位区分が異なる)に存在するケースの誤爆を避ける。
  const buildRenameFollowedParentPaths = (
    target: LocationDivisionShape,
    oldName: string,
    newName: string,
    basePaths: LocationDivisionParentPathBase[],
  ): LocationDivisionParentPathBase[] | null => {
    if (oldName === newName) return null;
    const allPaths = basePaths;
    const allDivisions = db.locationDivisions ?? [];
    const divisionById = new Map(allDivisions.map((d) => [d.id, d]));
    const targetAncestorPath = pathMapFor(allPaths, target.id);
    const updated = allPaths.map((p) => {
      if (p.ancestor_slot !== target.slot || p.ancestor_division_name !== oldName || p.deleted_at != null) {
        return p;
      }
      const owner = divisionById.get(p.location_division_id);
      if (!owner || owner.area_division_id !== target.area_division_id) {
        return p;
      }
      const ownerPath = pathMapFor(allPaths, owner.id);
      if (!matchesAncestorPrefix(ownerPath, targetAncestorPath)) {
        return p;
      }
      return { ...p, ancestor_division_name: newName, updatedAt: Time.Now().GetTime() };
    });
    const changed = updated.some((p, i) => p !== allPaths[i]);
    if (!changed) return null;
    return updated;
  };

  const openNew = async () => {
    const defaults = config.newDefaults(db);
    const newId = await nextIdFor(idb, ownerId, config.collectionKey);
    const now = Time.Now().GetTime();
    const initial: Draft = { ...draftFromDefaults(defaults), id: newId, createdAt: now, updatedAt: now, deleted_at: null };
    if (partitionKey && filterValue !== null) {
      initial[partitionKey] = filterValue;
    }
    if (isAreaDivision) {
      initial.location_labels = buildLocationLabelsDraft(null);
    }
    if (isLocationDivision) {
      initial.parent_path_draft = undefined;
    }
    setEditing(initial);
    setIsNew(true);
  };

  // 通常マスタ: シンプル複製。区分マスタ: 確認ダイアログを開く
  const duplicateRow = async (row: T) => {
    if (isDivisionMaster) {
      setDivisionDupConfirm(row);
      return;
    }
    const newId = await nextIdFor(idb, ownerId, config.collectionKey);
    const now = Time.Now().GetTime();
    const copy: T = { ...row, id: newId, createdAt: now, updatedAt: now, deleted_at: null, ...buildCopyNamePatch(row) };
    await replaceCollections(idb, ownerId, config.patchOf([...allRows, copy]), pageContext.api);
  };

  // 区分マスタ複製: 区分自体 + 紐づく全データを新区分ID で複製。
  // 複製計画(ID再採番・外部キー再配線)は divisionDuplication.ts の純関数が組み立てる。
  const confirmDuplicateDivision = async (row: T) => {
    if (!divisionPartitionKey) return;
    const dpk = divisionPartitionKey;
    const oldDivId = row.id;

    const newDivId = await nextIdFor(idb, ownerId, config.collectionKey);
    const nowMs = Time.Now().GetTime();
    const newDivision: T = {
      ...row,
      id: newDivId,
      createdAt: nowMs,
      updatedAt: nowMs,
      deleted_at: null,
      ...buildCopyNamePatch(row),
    };

    const duplicableCollections: Array<readonly [keyof ExtendedDB, ReadonlyArray<Record<string, unknown>>]> = [];
    for (const key of objectKeys(db)) {
      const v = db[key];
      if (Array.isArray(v) && key !== config.collectionKey) {
        const collectionRows: Array<Record<string, unknown>> = v;
        duplicableCollections.push([key, collectionRows]);
      }
    }
    const matchingByCollection = collectDivisionChildRows(duplicableCollections, dpk, oldDivId);
    const nextIdByCollection = new Map<keyof ExtendedDB, number>();
    for (const key of matchingByCollection.keys()) {
      nextIdByCollection.set(key, await nextIdFor(idb, ownerId, key));
    }
    const collectionCopies = buildDivisionDuplication({
      matchingByCollection,
      nextIdByCollection,
      partitionKey: dpk,
      newDivisionId: newDivId,
      nowMs,
    });

    let updates: SlicePatch = { ...config.patchOf([...allRows, newDivision]) };
    const rowsByCollection = new Map(duplicableCollections);
    for (const [k2, copies2] of collectionCopies) {
      const existing = rowsByCollection.get(k2) ?? [];
      updates = { ...updates, ...slicePatchOfRecords(k2, [...existing, ...copies2].map(withRecordId)) };
    }
    try {
      await replaceCollections(idb, ownerId, updates, pageContext.api);
    } catch (e) {
      pageContext.api.showError(`${divisionLabel}の複製に失敗しました：${errorMessageOf(e)}`);
      return;
    }
    setDivisionDupConfirm(null);
  };

  // 区分マスタ削除: 区分自体 + 紐づく全データを論理削除
  const confirmDeleteDivision = async (row: T) => {
    if (!divisionPartitionKey) return;
    const dpk = divisionPartitionKey;
    const divId = row.id;
    const nowMs = Time.Now().GetTime();
    let updates: SlicePatch = {};
    for (const key of objectKeys(db)) {
      const v = db[key];
      if (Array.isArray(v) && key !== config.collectionKey) {
        const collectionRows: Array<Record<string, unknown>> = v;
        const updated = collectionRows.map((r) => {
          if (readNumberProp(r, dpk) === divId && !isDeleted(r)) {
            return { ...r, deleted_at: nowMs };
          }
          return r;
        });
        if (updated.some((r, i) => r !== collectionRows[i])) {
          updates = { ...updates, ...slicePatchOfRecords(key, updated.map(withRecordId)) };
        }
      }
    }
    const selfUpdated = allRows.map((r) => {
      if (r.id === divId) {
        return { ...r, deleted_at: nowMs };
      }
      return r;
    });
    updates = { ...updates, ...config.patchOf(selfUpdated) };
    try {
      await replaceCollections(idb, ownerId, updates, pageContext.api);
    } catch (e) {
      pageContext.api.showError(`${divisionLabel}の削除に失敗しました：${errorMessageOf(e)}`);
      return;
    }
    setConfirmDelete(null);
  };

  const openEdit = (row: T) => {
    const initial: Draft = { ...row };
    if (isAreaDivision) {
      initial.location_labels = buildLocationLabelsDraft(row.id);
    }
    if (isLocationDivision) {
      initial.parent_path_draft = buildParentPathDraft(row.id);
    }
    setEditing(initial);
    setIsNew(false);
  };

  const close = () => {
    setEditing(null);
    setIsNew(false);
  };

  // required: true のフィールドが未入力なら label を返す。visible で非表示のフィールドは検証外。
  const findMissingRequiredLabel = (draft: Draft): string | null => {
    for (const field of config.fields) {
      const isHidden = field.visible !== undefined && !field.visible(draft, db);
      const needsCheck = field.required === true && !isHidden;
      const value = readProp(draft, field.key);
      const isEmpty = value === "" || value === undefined || value === null;
      if (needsCheck && isEmpty) {
        return field.label;
      }
    }
    return null;
  };

  // AreaDivision の保存時に、ドラフトの location_labels を
  // areaDivisionLocationLabels コレクション全体へ反映する。
  // ・他の区分に属する既存ラベルは保持
  // ・対象区分の既存ラベルは (slot) で突き合わせて id を維持
  // ・label_name が空 かつ is_required が false のスロットは出力しない(=削除扱い)
  // ・新規スロットは末尾連番で id を採番
  const buildSyncedLocationLabels = async (areaDivisionId: number, draftLabels: Array<Pick<AreaDivisionLocationLabelBase, "slot" | "label_name" | "is_required">>): Promise<AreaDivisionLocationLabelBase[]> => {
    const nowMs = Time.Now().GetTime();
    const allLabels = db.areaDivisionLocationLabels ?? [];
    const otherAreas = allLabels.filter((l) => l.area_division_id !== areaDivisionId);
    const sameAreaExisting = allLabels.filter((l) => l.area_division_id === areaDivisionId);
    const existingBySlot = new Map<number, AreaDivisionLocationLabelBase>();
    for (const l of sameAreaExisting) {
      existingBySlot.set(l.slot, l);
    }
    let nextLabelId = await nextIdFor(idb, ownerId, "areaDivisionLocationLabels");
    const syncedForArea: AreaDivisionLocationLabelBase[] = [];
    for (const d of draftLabels) {
      const prev = existingBySlot.get(d.slot);
      const isEmpty = d.label_name === "" && !d.is_required;
      if (isEmpty) {
        // 既存があれば論理削除として持ち越す(deleted_at をスタンプ)。新規なら出力しない。
        if (prev && prev.deleted_at == null) {
          syncedForArea.push({ ...prev, label_name: "", is_required: false, deleted_at: nowMs, updatedAt: nowMs });
        } else if (prev) {
          syncedForArea.push(prev);
        }
        continue;
      }
      if (prev) {
        syncedForArea.push({
          ...prev,
          label_name: d.label_name,
          is_required: d.is_required,
          deleted_at: null,
          updatedAt: nowMs,
        });
      } else {
        const newRow: AreaDivisionLocationLabelBase = {
          id: nextLabelId,
          createdAt: nowMs,
          updatedAt: nowMs,
          area_division_id: areaDivisionId,
          slot: d.slot,
          label_name: d.label_name,
          is_required: d.is_required,
          deleted_at: null,
        };
        syncedForArea.push(newRow);
        nextLabelId += 1;
      }
    }
    return [...otherAreas, ...syncedForArea];
  };

  const save = async () => {
    if (!editing) return;
    const missingLabel = findMissingRequiredLabel(editing);
    if (missingLabel !== null) {
      pageContext.api.showError(`「${missingLabel}」は必須項目です。入力してください。`);
      return;
    }
    const cur = allRows;
    const nowMs = Time.Now().GetTime();
    // location_labels / parent_path_draft はドラフトの一時フィールドのため、本体には保存しない。
    const draftLocationLabels: Array<Pick<AreaDivisionLocationLabelBase, "slot" | "label_name" | "is_required">> | null = (() => {
      if (isAreaDivision && Array.isArray(editing.location_labels)) {
        return editing.location_labels;
      }
      return null;
    })();
    // 全スロットを空にすると parent_path_draft は undefined になる(ParentPathEditor 参照)。
    // その場合も既存 locationDivisionParentPaths を論理削除する必要があるため、isLocationDivision
    // であれば常に同期対象にする({} を渡し「全スロット空」として扱わせる)。
    const draftParentPath: Record<number, string> | null = isLocationDivision
      ? (editing.parent_path_draft ?? {})
      : null;
    const oldDivisionForRename: LocationDivisionBase | null = (() => {
      if (!isLocationDivision || isNew) return null;
      return (db.locationDivisions ?? []).find((r) => r.id === editing.id) ?? null;
    })();
    const stamped: Draft = { ...editing, updatedAt: nowMs };
    delete stamped.location_labels;
    delete stamped.parent_path_draft;
    const next = (() => {
      if (isNew) return [...cur, stamped];
      return cur.map((r) => {
        if (r.id === editing.id) {
          return stamped;
        }
        return r;
      });
    })();
    let writes: SlicePatch = { ...config.patchOf(next) };
    if (isAreaDivision && draftLocationLabels) {
      writes.areaDivisionLocationLabels = await buildSyncedLocationLabels(editing.id, draftLocationLabels);
    }
    if (isLocationDivision) {
      // 改名追従と上位区分パス同期は同じ locationDivisionParentPaths を書き換えるため、
      // 同一のベース配列からメモリ上で1つの配列に合成し、保存(replaceCollections)は1回だけにする。
      // 別々に replaceCollection すると、後勝ちの書き込みが先の更新を丸ごと上書きして消してしまう。
      const basePaths = db.locationDivisionParentPaths ?? [];
      // 改名追従: 保存後の本体で division_name が変わっていれば、同一 area_division_id かつ
      // 旧フルパス prefix 一致の子孫の parent_path を新名称へ更新する。
      const stampedDivision = locationDivisionShapeOf(stamped);
      const renameFollowed = oldDivisionForRename && stampedDivision !== null && oldDivisionForRename.division_name !== stampedDivision.division_name
        ? buildRenameFollowedParentPaths(stampedDivision, oldDivisionForRename.division_name, stampedDivision.division_name, basePaths)
        : null;
      const mergedBase = renameFollowed ?? basePaths;
      // draftParentPath は isLocationDivision であれば常に非null({}を含む)なので必ず同期を通す。
      // 新規保存直後は本体保存で ID が確定しているため、そのまま parent path を続けて保存できる。
      writes.locationDivisionParentPaths = await buildSyncedParentPaths(editing.id, draftParentPath ?? {}, mergedBase);
    }
    await replaceCollections(idb, ownerId, writes, pageContext.api);
    close();
  };

  // 論理削除: deleted_at にタイムスタンプを設定して残置
  const remove = async (row: T) => {
    const nowMs = Time.Now().GetTime();
    const next = allRows.map((r) => {
      if (r.id === row.id) {
        return { ...r, deleted_at: nowMs };
      }
      return r;
    });
    await replaceCollections(idb, ownerId, config.patchOf(next), pageContext.api);
    setConfirmDelete(null);
  };

  const editingField = (field: FieldDef<T>) => {
    if (!editing) return null;
    const currentDraft = editing;
    if (field.visible && !field.visible(currentDraft, db)) return null;
    const value = readProp(currentDraft, field.key);
    const setValue = (v: unknown) => setEditing({ ...currentDraft, [String(field.key)]: v });
    const opts = field.options !== undefined ? field.options.resolve(db, currentDraft) : [];
    const textValue = (() => {
      if (typeof value === "string" || typeof value === "number") {
        return value;
      }
      return "";
    })();
    switch (field.type) {
      case "text":
        return <TextField fullWidth size="small" label={field.label} value={textValue} onChange={(e) => setValue(e.target.value)} required={field.required} helperText={field.helperText} placeholder={field.placeholder} />;
      case "number":
        return <TextField fullWidth size="small" type="number" label={field.label} value={textValue} onChange={(e) => setValue(e.target.value === "" ? null : Number(e.target.value))} required={field.required} helperText={field.helperText} placeholder={field.placeholder} />;
      case "multiline":
        return <TextField fullWidth size="small" multiline minRows={2} label={field.label} value={textValue} onChange={(e) => setValue(e.target.value)} helperText={field.helperText} placeholder={field.placeholder} />;
      case "boolean":
        return <FormControlLabel control={<Switch checked={!!value} onChange={(e) => setValue(e.target.checked)} />} label={field.label} />;
      case "select":
        return (
          <TextField select fullWidth size="small" label={field.label} value={textValue} onChange={(e) => setValue(e.target.value)} required={field.required} helperText={field.helperText}>
            {opts.map((o) => <MenuItem key={String(o.value)} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        );
      case "custom":
        return field.customRender ? <>{field.customRender({ draft: currentDraft, setDraft: setEditing, db })}</> : null;
    }
  };

  const pickPartitionLabel = (): string | null => {
    if (partitionKey === "area_division_id") return "工事型区分";
    if (partitionKey === "material_division_id") return "材料区分";
    return null;
  };
  const partitionLabel = pickPartitionLabel();

  if (loading) {
    return <ContentLoading />;
  }

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" gap={1.5}>
        {partitionKey && partitionLabel && (
          <>
            <Typography variant="body2" color="text.secondary">{partitionLabel} で絞り込み:</Typography>
            <TextField
              select
              size="small"
              value={filterValue ?? ""}
              onChange={(e) => setFilterValue(e.target.value === "" ? null : Number(e.target.value))}
              sx={{ minWidth: 220 }}
            >
              {partitionOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              （{visibleRows.length} / 全 {rows.length} 件）
            </Typography>
          </>
        )}
        <Box flex={1} />
        <Button variant="contained" startIcon={<AddIcon />} disabled={!isOnline} onClick={openNew}>新規追加</Button>
      </Stack>

      <Paper variant="outlined">
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {visibleColumns.map((c) => (
                  <TableCell key={String(c.key)} sx={{ fontWeight: "bold", width: c.width }}>{c.label}</TableCell>
                ))}
                <TableCell sx={{ width: 130 }}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row: T) => (
                <TableRow key={row.id} hover>
                  {visibleColumns.map((c) => (
                    <TableCell key={String(c.key)}>
                      {c.render ? c.render(row, db) : String(readProp(row, c.key) ?? "")}
                    </TableCell>
                  ))}
                  <TableCell>
                    <IconButton size="small" disabled={!isOnline} onClick={() => openEdit(row)} title="編集"><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" disabled={!isOnline} onClick={() => duplicateRow(row)} title="複製"><ContentCopyIcon fontSize="small" /></IconButton>
                    <IconButton size="small" disabled={!isOnline} onClick={() => setConfirmDelete(row)} title="削除"><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    {partitionKey
                      ? `この${partitionLabel}にはまだデータがありません。「新規追加」から登録してください。`
                      : "データがまだありません。「新規追加」から登録してください。"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Dialog open={!!editing} onClose={close} fullWidth maxWidth={config.customEditContent ? "md" : "sm"}>
        <DialogTitle>{isNew ? `${config.title} を追加` : `${config.title} を編集`}</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            {config.fields.map((f) => (
              <Box key={String(f.key)}>{editingField(f)}</Box>
            ))}
            {editing && config.customEditContent && (
              <Box>
                {config.customEditContent({ draft: editing, setDraft: setEditing, db })}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>キャンセル</Button>
          <Button variant="contained" disabled={!isOnline} onClick={save}>保存</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} fullWidth maxWidth="sm">
        <DialogTitle>{isDivisionMaster ? `${divisionLabel}の削除確認` : "削除確認"}</DialogTitle>
        <DialogContent>
          {isDivisionMaster && confirmDelete ? (() => {
            const divId = confirmDelete.id;
            const name = readNameProp(confirmDelete);
            const { entries, total } = countRelated(divId);
            return (
              <Stack gap={1.5}>
                <Typography>
                  {divisionLabel}「<b>#{divId} {name}</b>」を論理削除します（ID は再採番されません）。
                </Typography>
                {total > 0 ? (
                  <>
                    <Typography color="error.main" fontWeight="bold">
                      ⚠️ この{divisionLabel}に紐づく以下のデータも<u>すべて論理削除</u>されます:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, my: 0 }}>
                      {entries.map((e) => (
                        <li key={String(e.key)}>
                          <Typography variant="body2">{e.label}: <b>{e.count}件</b></Typography>
                        </li>
                      ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary">合計 {total} 件 + {divisionLabel}本体 1件</Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">この{divisionLabel}に紐づくデータはありません。</Typography>
                )}
                <Typography color="warning.main">削除してもデータは内部に保持されます。</Typography>
              </Stack>
            );
          })() : isLocationDivision && confirmDelete ? (() => {
            const targetDivision = locationDivisionShapeOf(confirmDelete);
            const descendants = targetDivision !== null ? findLocationDivisionDescendants(targetDivision) : [];
            return (
              <Stack gap={1.5}>
                <Typography>このデータを削除します。よろしいですか？</Typography>
                {descendants.length > 0 && (
                  <>
                    <Typography color="error.main" fontWeight="bold">
                      ⚠️ 以下の場所区分が、この区分を上位区分パスに含んでいます(削除しても連動して消えません):
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, my: 0 }}>
                      {descendants.map((d) => (
                        <li key={d.id}><Typography variant="body2">#{d.id} {d.division_name}</Typography></li>
                      ))}
                    </Box>
                  </>
                )}
              </Stack>
            );
          })() : (
            <Typography>このデータを削除します。よろしいですか？</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>キャンセル</Button>
          <Button color="error" variant="contained" disabled={!isOnline} onClick={() => {
            if (!confirmDelete) return;
            if (isDivisionMaster) confirmDeleteDivision(confirmDelete);
            else remove(confirmDelete);
          }}>削除</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!divisionDupConfirm} onClose={() => setDivisionDupConfirm(null)} fullWidth maxWidth="sm">
        <DialogTitle>{divisionLabel}の複製確認</DialogTitle>
        <DialogContent>
          {divisionDupConfirm && (() => {
            const divId = divisionDupConfirm.id;
            const name = readNameProp(divisionDupConfirm);
            const { entries, total } = countRelated(divId);
            const allIds = allRows.map((r) => r.id);
            const maxId = (): number => {
              if (allIds.length === 0) return 0;
              return Math.max(...allIds);
            };
            const newId = maxId() + 1;
            return (
              <Stack gap={1.5}>
                <Typography>
                  {divisionLabel}「<b>#{divId} {name}</b>」を複製します。
                </Typography>
                <Typography variant="body2">
                  新 ID: <b>#{newId}</b> / 新名称: <b>{name} (コピー)</b>（複製後に編集可能）
                </Typography>
                {total > 0 ? (
                  <>
                    <Typography color="warning.main" fontWeight="bold">
                      この{divisionLabel}に紐づく以下のデータも<u>まとめて複製</u>されます:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, my: 0 }}>
                      {entries.map((e) => (
                        <li key={String(e.key)}>
                          <Typography variant="body2">{e.label}: <b>{e.count}件</b></Typography>
                        </li>
                      ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      合計 {total} 件 + {divisionLabel}本体 1件。関連するデータのつながりも自動で引き継がれます。
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    この{divisionLabel}に紐づくデータはありません。区分本体のみ複製します。
                  </Typography>
                )}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDivisionDupConfirm(null)}>キャンセル</Button>
          <Button variant="contained" disabled={!isOnline} onClick={() => divisionDupConfirm && confirmDuplicateDivision(divisionDupConfirm)}>
            複製する
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );

  // ===== 動的キー駆動ゆえに閉じた型で表せない箇所の局所ヘルパ =====

  // newDefaults の Partial<T> をドラフト初期値として受ける。直後に id/createdAt/updatedAt/deleted_at を
  // 必ず上書きするため、bounds 必須項目の欠落は起きない。
  function draftFromDefaults(defaults: Partial<T>): Draft {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Partial からの初期化直後に bounds 必須項目を全て設定する前提の境界
    return defaults as Draft;
  }
}

// 場所区分マスタの特殊処理(改名追従・子孫警告)が必要とするフィールドの形状。
// bounds 行から実行時検査で取り出す(表明を使わず、フィールドが無ければ null)。
type LocationDivisionShape = { id: number; slot: number; division_name: string; area_division_id: number };

function locationDivisionShapeOf(row: MasterRowBounds): LocationDivisionShape | null {
  const slot = readProp(row, "slot");
  const divisionName = readProp(row, "division_name");
  const areaDivisionId = readProp(row, "area_division_id");
  if (typeof slot === "number" && typeof divisionName === "string" && typeof areaDivisionId === "number") {
    return { id: row.id, slot, division_name: divisionName, area_division_id: areaDivisionId };
  }
  return null;
}

// 複製時の「(コピー)」名称付与。名称系フィールドのうち最初に値を持つ1つへ付与する(旧実装と同順)。
function buildCopyNamePatch(row: MasterRowBounds): Partial<Record<"name" | "definition_name" | "type_name" | "division_name", string>> {
  const patch: Partial<Record<"name" | "definition_name" | "type_name" | "division_name", string>> = {};
  for (const key of ["name", "definition_name", "type_name", "division_name"] as const) {
    const current = readProp(row, key);
    if (typeof current === "string" && current.length > 0) {
      patch[key] = `${current} (コピー)`;
      return patch;
    }
  }
  return patch;
}

// レコード行に id(number | string)があることを実行時に確認して返す(動的コレクション書き戻し用)。
function withRecordId(record: Record<string, unknown>): Record<string, unknown> & { id: number | string } {
  const id = record.id;
  if (typeof id === "number" || typeof id === "string") {
    return { ...record, id };
  }
  throw new Error("行に id がありません(データ不整合)。");
}
